
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter, Mutation } from 'vuex-class';

import DirectionsSvg from '@/components/DirectionsSvg.vue';
import PhoneSvg from '@/components/PhoneSvg.vue';
import { MutationTypes, OrderMethods, TimeModes } from '@/enums';
import UserModule from '@/store/modules/user';
import { addHoursToDate, formatPhoneNumber, getOrderMethod, parseDate } from '@/utils/global.util';

const { SET_EDIT_IS_OPEN } = MutationTypes;

@Component({
  components: {
    DirectionsSvg,
    PhoneSvg,
  },
})
export default class OrderLocationDetails extends Vue {
  @Prop() allowUpdates: boolean;
  @Prop({ type: Object, default: null }) deliveryAddress: DeliveryAddress | null;
  @Prop({ type: Object, default: null }) restaurant: Restaurant;
  @Prop({ type: String, default: '' }) deliveryMode: string;
  @Prop({ type: String, default: '' }) readyTime: string;
  @Prop({ type: String, default: '' }) timeMode: string;
  @Prop({ type: String, default: '' }) timePlaced: string;
  @Getter editIsOpen: boolean;
  @Mutation(SET_EDIT_IS_OPEN) setEditIsOpen: (action: boolean) => void;

  setDefaultAddressClicked: boolean;
  isFavorite: boolean;
  isDefault: boolean;
  data() {
    return {
      setDefaultAddressClicked: false,
      isFavorite: false,
      isDefault: false,
    }
  }

  mounted() {
    this.handlefavoriteRestaurantsChange();
  }

  get isDelivery() {
    return this.deliveryMode === OrderMethods.DELIVERY || this.deliveryMode === OrderMethods.DISPATCH || false;
  }

  get orderMethod() {
    return getOrderMethod(this.deliveryMode);
  }

  get today(): Date {
    // return new Date('2021-09-11T12:10:00'); // used to spoof times for testing
    return new Date();
  }

  get tomorrow(): Date {
    return addHoursToDate(this.today, 24);
  }

  get todayValue(): string {
    return `${this.today.getFullYear()}-${this.today.getMonth() + 1}-${this.today.getDate()}`;
  }

  get tomorrowValue(): string {
    return `${this.tomorrow.getFullYear()}-${this.tomorrow.getMonth() + 1}-${this.tomorrow.getDate()}`;
  }

  get readyTimeText() {
    if (this.readyTime) {
      const parsedDate = parseDate(this.readyTime) as Date;
      const parsedDateValue = `${parsedDate.getFullYear()}-${parsedDate.getMonth() + 1}-${parsedDate.getDate()}`;
      if (parsedDateValue === this.todayValue) {
        return `Ready at ${parsedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      } else if (parsedDateValue === this.tomorrowValue) {
        return `Ready tomorrow at ${parsedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        return `Ready ${parsedDate.getMonth() + 1}/${parsedDate.getDate()} at ${parsedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      }
    }
    return null;
  }

  get timePlacedText() {
    if (this.timePlaced) {
      return parseDate(this.timePlaced);
    }
    return null;
  }

  get locationLineOne() {
    if (this.isDelivery) {
      return this.deliveryAddress?.streetAddress;
    }
    return this.restaurant.name;
  }

  get locationLineTwo() {
    if (this.isDelivery) {
      return this.deliveryAddress?.building;
    }
    return this.restaurant.street;
  }

  get locationLineThree() {
    if (this.isDelivery) {
      return `${this.deliveryAddress?.city}, ${this.deliveryAddress?.zipCode}`;
    }
    return `${this.restaurant.city} ${this.restaurant.state}, ${this.restaurant.zip}`;
  }

  get locationPhoneNumber() {
    if (this.isDelivery) {
      return formatPhoneNumber(this.deliveryAddress?.phoneNumber);
    }
    return this.restaurantPhoneNumber;
  }

  get restaurantPhoneNumber() {
    return formatPhoneNumber(this.restaurant.phoneNumber);
  }

  get isDefaultAddressSelected() {
    return this.deliveryAddress?.isDefault;
  }

  get favoriteRestaurants() {
    return UserModule.favoriteRestaurants || {};
  }

  @Watch('favoriteRestaurants')
  handlefavoriteRestaurantsChange() {
    const store = this.favoriteRestaurants.find((restaurant) => restaurant.id === this.restaurant.id);
    if (store) {
      this.isFavorite = true;
      this.isDefault = store.isDefault;
    } else {
      this.isFavorite = false;
      this.isDefault = false;
    }
  }

  get isLoggedIn() {
    return UserModule.loggedIn;
  }

  handleEditClick() {
    this.setEditIsOpen(true);
  }

  get deliveryAddressId() {
    return this.deliveryAddress?.id || 0;
  }

  get restaurantId() {
    return this.restaurant.id;
  }

  get orderTimeMethod() {
    return this.timeMode === TimeModes.ADVANCE ? 'scheduled' : this.timeMode;
  }

  get telephoneRef() {
    return `tel:${this.restaurantPhoneNumber}`;
  }

  get addressRef(): string {
    return `http://google.com/maps/place/${this.restaurant.street},${this.restaurant.city},${this.restaurant.state},${this.restaurant.zip}`;
  }

  async handleDefaultAddressClick() {
    try {
      this.setDefaultAddressClicked = true;
      await UserModule.setDefaultDeliveryAddress(this.deliveryAddressId);
    } catch (err) {
      this.setDefaultAddressClicked = false;
      this.$log.error(err);
      this.$toasted.error(err.data);
    }
  }

  async submitMakeFavorite() {
    try {
      const favoriteModel = await UserModule.addFavoriteLocation({ restaurantId: this.restaurant.id, isDefault: false }) as FavoriteLocationModel;
      if (favoriteModel) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { actionButtonText, ...rest } = this.$props as any;
        const addedLocation: FavoriteRestaurant = {
          ...this.restaurant,
          isDefault: favoriteModel.isDefault,
          favoriteId: favoriteModel.id
        };
        UserModule.addFavoriteLocationToCustomer(addedLocation);
      }
    } catch (err) {
      this.$log.error(err);
      this.$toasted.error(err.data);
    }
  }

  async submitMakeDefault() {
    try {
      const favoriteModel = await UserModule.addFavoriteLocation({ restaurantId: this.restaurant.id, isDefault: true }) as FavoriteLocationModel;
      if (favoriteModel) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { actionButtonText, ...rest } = this.$props as any;
        const addedLocation: FavoriteRestaurant = {
          ...this.restaurant,
          isDefault: favoriteModel.isDefault,
          favoriteId: favoriteModel.id
        };
        UserModule.addFavoriteLocationToCustomer(addedLocation);
      }
    } catch (err) {
      this.$log.error(err);
      this.$toasted.error(err.data);
    }
  }

  async submitRemoveFromFavorites() {
    const favoriteRestaurant = UserModule.favoriteRestaurants.find((restaurant) => restaurant.id === this.restaurant.id);
    if (favoriteRestaurant) {
      try {
        if (favoriteRestaurant) {
          UserModule.removeFavoriteLocationFromCustomer(favoriteRestaurant);
          await UserModule.removeFavoriteLocation(favoriteRestaurant.favoriteId);
        }
      } catch (err) {
        UserModule.addFavoriteLocationToCustomer(favoriteRestaurant);
        this.$log.error(err);
        this.$toasted.error(err.data);
      }
    }
  }
}
